/**
 * Pending counts - the "needs attention" numbers behind the bell and the
 * sidebar badges.
 *
 * One endpoint, one cached query: the bell in the header and every badge in
 * the nav read the same `usePendingCountsQuery`, so RTK issues a single
 * request on a 60s poll no matter how many places display it.
 *
 * These are real server-side counts of work waiting, NOT an unread/"new
 * since you last looked" feed - the backend has no per-module read state.
 *
 * `total` sums the four notification-panel action cards only (excludes
 * `applications`, which remains for sidebar badges).
 */
import { baseApi } from "@/app/store/api/baseApi";

export interface PendingCounts {
  /** Open cases in follow-up status with check-in date on or before today. */
  followups: number;
  /** Org applications awaiting review (control plane). */
  applications: number;
  /** Overdue open tasks (legacy sidebar key). */
  tasks: number;
  /** Awarded grants with a reporting / next-action date in the past. */
  grant_renewals: number;
  /** Service deliveries awaiting manager approval. */
  task_approvals: number;
  /** Submitted timesheets plus unverified volunteer shifts. */
  hours_pending: number;
  total: number;
}

const ZERO: PendingCounts = {
  followups: 0,
  applications: 0,
  tasks: 0,
  grant_renewals: 0,
  task_approvals: 0,
  hours_pending: 0,
  total: 0,
};

function normalizePendingCounts(raw: Partial<PendingCounts> | null | undefined): PendingCounts {
  const n = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : 0);
  const counts: PendingCounts = {
    followups: n(raw?.followups),
    applications: n(raw?.applications),
    tasks: n(raw?.tasks),
    grant_renewals: n(raw?.grant_renewals),
    task_approvals: n(raw?.task_approvals),
    hours_pending: n(raw?.hours_pending),
    total: n(raw?.total),
  };
  if (counts.total === 0) {
    counts.total =
      counts.followups + counts.grant_renewals + counts.task_approvals + counts.hours_pending;
  }
  return counts;
}

export const pendingCountsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    pendingCounts: builder.query<PendingCounts, void>({
      query: () => "/manifest/pending-counts/",
      transformResponse: (r: Partial<PendingCounts> | null) => normalizePendingCounts(r),
      providesTags: ["PendingCounts"],
    }),
  }),
});

export const { usePendingCountsQuery } = pendingCountsApi;

export { ZERO as emptyPendingCounts, normalizePendingCounts };
